import { useEffect, useRef, useState } from "react";
import GameShell from "../GameShell";

/**
 * Color Forest — Stroop test.
 * 16 trials. Show a color-word painted in a possibly different ink.
 * Child taps the button matching THE INK COLOR.
 */
export default function ColorForest() {
  return (
    <GameShell gameId="color_forest" title="Floresta das Cores">
      {({ onFinish }) => <StroopGame onFinish={onFinish} />}
    </GameShell>
  );
}

const COLORS = [
  { name: "Vermelho", hex: "#EF4444" },
  { name: "Azul", hex: "#3B82F6" },
  { name: "Verde", hex: "#10B981" },
  { name: "Amarelo", hex: "#F59E0B" },
];
const TRIALS = 16;

function StroopGame({ onFinish }) {
  const [trial, setTrial] = useState(0);
  const [current, setCurrent] = useState(null);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const startedAt = useRef(performance.now());
  const trialStart = useRef(performance.now());

  const nextTrial = (idx) => {
    if (idx >= TRIALS) return;
    // pick a random word and a random ink (50% mismatch)
    const word = COLORS[Math.floor(Math.random() * COLORS.length)];
    let ink = word;
    if (Math.random() < 0.7) {
      do {
        ink = COLORS[Math.floor(Math.random() * COLORS.length)];
      } while (ink.name === word.name);
    }
    setCurrent({ word, ink });
    trialStart.current = performance.now();
  };

  useEffect(() => {
    nextTrial(0);
  }, []);

  const answer = (color) => {
    if (!current || feedback) return;
    const rt = performance.now() - trialStart.current;
    const correct = color.name === current.ink.name;
    setReactionTimes((s) => [...s, rt]);
    if (correct) setHits((h) => h + 1);
    else setMisses((m) => m + 1);
    setFeedback(correct ? "✅" : "❌");
    setTimeout(() => {
      setFeedback(null);
      const next = trial + 1;
      setTrial(next);
      if (next >= TRIALS) {
        const elapsed = performance.now() - startedAt.current;
        const totalHits = hits + (correct ? 1 : 0);
        const totalMisses = misses + (correct ? 0 : 1);
        const allRts = [...reactionTimes, rt];
        const avg = allRts.reduce((a, b) => a + b, 0) / allRts.length;
        const score = Math.max(0, Math.round(totalHits * 10 - totalMisses * 4));
        onFinish({
          score,
          duration_ms: Math.round(elapsed),
          metrics: {
            hits: totalHits,
            misses: totalMisses,
            trials: TRIALS,
            avg_reaction_ms: Math.round(avg),
            accuracy: Number((totalHits / TRIALS).toFixed(3)),
          },
        });
      } else {
        nextTrial(next);
      }
    }, 450);
  };

  if (!current) return null;

  return (
    <div className="mt-4 max-w-2xl mx-auto" data-testid="stroop-area">
      <div className="text-center mb-4 text-white drop-shadow">
        <div className="text-sm">Toque na COR em que a palavra está pintada.</div>
        <div className="text-xs mt-1 opacity-80">Rodada {Math.min(trial + 1, TRIALS)} / {TRIALS}</div>
      </div>

      <div className="kid-card flex items-center justify-center h-44 pop-in" key={trial}>
        <div
          className="font-extrabold tracking-tight"
          style={{ color: current.ink.hex, fontSize: "5rem", fontFamily: "Fredoka, sans-serif" }}
          data-testid="stroop-word"
        >
          {current.word.name}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        {COLORS.map((c) => (
          <button
            key={c.name}
            onClick={() => answer(c)}
            className="kid-btn text-white text-lg"
            style={{ background: c.hex }}
            data-testid={`stroop-btn-${c.name}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {feedback && (
        <div className="text-center text-5xl mt-4 pop-in" data-testid="stroop-feedback">
          {feedback}
        </div>
      )}
    </div>
  );
}
